import React, { createContext, useContext, useState, ReactNode } from "react";

export type Language = "en" | "ro";

interface Translations {
  en: {
    [key: string]: string;
  };
  ro: {
    [key: string]: string;
  };
}

const translations: Translations = {
  en: {
    // Header
    agentName: "Premium Oombla Travel Agent",
    register: "Register",
    login: "Login",
    minimizeChat: "Minimize chat",
    closeChat: "Close chat",
    openUserMenu: "Open user menu",
    signedInAs: "Signed in as {email}",
    manageUser: "Manage user",
    signOut: "Sign out",
    user: "User",
    settings: "Settings",
    accountTitle: "Account",
    accountSubtitle: "Update your profile information",
    avatarUrl: "Avatar URL",
    saveChanges: "Save changes",
    saved: "Saved!",
    close: "Close",

    // Chat Interface
    chatPlaceholder: "Ask me anything about your next travel destination...",
    send: "Send",
    conversationsHistory: "Conversations History",

    // Conversations History
    conversationsTitle: "Conversations History",
    conversation1: "Trip to Japan - Cherry Blossom Season",
    conversation2: "European Summer Adventure",
    backToChat: "Back to Current Chat",

    // Destinations
    paris: "Paris",
    tokyo: "Tokyo",
    newYork: "New York",
    parisDesc:
      "The City of Light awaits with its romantic charm and world-class cuisine.",
    tokyoDesc:
      "Experience the perfect blend of traditional culture and modern innovation.",
    newYorkDesc:
      "The city that never sleeps offers endless possibilities and iconic landmarks.",

    // Auth dialog
    authDialogTitle: "Authentication",
    authDialogDescription: "Sign in or create an account",
    processing: "Processing...",

    // Login / Register forms
    welcomeBack: "Welcome back",
    email: "Email",
    emailPlaceholder: "your@email.com",
    password: "Password",
    passwordPlaceholder: "Your password",
    forgotPassword: "Forgot password?",
    loginAccount: "Sign in",
    submitLogin: "Signing in...",
    noAccount: "No account? Create one",
    joinJourney: "Join the journey",
    firstName: "First name",
    firstNamePlaceholder: "John",
    lastName: "Last name",
    lastNamePlaceholder: "Doe",
    createAccount: "Create account",
    submitRegister: "Creating account...",
    alreadyMember: "Already have an account? Sign in",

    // Confirm Email modal
    confirmEmailTitle: "Confirm your email",
    confirmEmailDescription:
      "The account for {email} is not confirmed. Please paste the confirmation token you received by email.",
    confirmationTokenPlaceholder: "Confirmation token",
    confirm: "Confirm",
    resendConfirmation: "Resend email",
    resendConfirmationHelp: "Send confirmation email again",
    confirmSuccess: "Email confirmed. You can sign in now.",
    resendSuccess: "Confirmation email has been resent. Check your inbox.",
    confirmingEmail: "Confirming your email…",
    confirmEmailError: "Email confirmation failed. The link may have expired.",
    genericError: "Something went wrong. Please try again.",

    // Account Sheet
    useDefaultAvatar: "Use default UI Avatar",
    usingDefaultAvatar: "Using default UI Avatar (read-only)",
    imgurOnlyHint: "Only imgur URLs are allowed",
    previewUrl: "Preview URL",
    deleteAccount: "Delete account",
    deleteAccountDesc: "Permanently remove your account and data.",
    confirmDelete: "Confirm delete",
    deleting: "Deleting...",
    passwordToDelete: "Insert your password to confirm",
    deleteError: "Could not delete the account. Check your password.",
    deleteConversationsError:
      "Could not delete conversations. Check your password.",
    deleteConversationsSuccess: "All conversations have been deleted.",
    deleteConversations: "Delete conversations",
    deleteConversationsDesc: "Remove all your chat history.",
    canNotBeUndone: "Operations below cannot be undone.",
    avatarHostError: "Only URLs from {hosts} are allowed.",

    // Forgot / Reset Password
    forgotPasswordTitle: "Forgot password",
    forgotPasswordDescription:
      "Enter your email address and we'll send you a link to reset your password.",
    sendResetLink: "Send reset link",
    resetEmailSent:
      "If your email exists in our system, you will receive a reset link shortly. Check your inbox.",
    backToLogin: "Back to login",
    resetPasswordTitle: "Reset password",
    resetPasswordDescription:
      "Paste the reset token from your email and choose a new password.",
    resetToken: "Reset token",
    resetTokenPlaceholder: "Paste the token from your email",
    newPassword: "New password",
    newPasswordPlaceholder: "Your new password",
    resetPassword: "Reset password",
    passwordResetSuccess:
      "Your password has been reset successfully. You can now log in.",
    resetPasswordError: "Invalid or expired token. Please try again.",
    haveResetToken: "Already have a reset token?",

    // Chat agent content
    initialWelcome:
      "Hey there! I'm your Premium Oombla Travel Agent. I'll take you beyond the beaten tourist path through careful planning and privileged connections, ensuring you an unforgettable experience. To get started choose an option below:",
    optionKnownDestinationTitle: "I know my travel destination",
    optionKnownDestinationDesc: "I have a specific place in mind",
    optionPartialReqTitle:
      "I don't know where, but I partially know what I want",
    optionPartialReqDesc: "I have some preferences but need guidance",
    connectErrorSend:
      "I'm having trouble connecting to the travel planning service. Please try again shortly.",
    connectErrorOption:
      "I'm having trouble connecting right now. Please try again in a moment.",
    conversationClosed: "This conversation is closed.",
    backToActiveChat: "Back to Active Chat",
    startNewChat: "Start New Chat",

    // Validations
    invalidEmail: "Invalid email address",
  },
  ro: {
    // Header
    agentName: "Agent Premium de Călătorii Oombla",
    register: "Înregistrare",
    login: "Autentificare",
    minimizeChat: "Minimizează chat-ul",
    closeChat: "Închide chat-ul",
    openUserMenu: "Deschide meniul utilizator",
    signedInAs: "Autentificat ca {email}",
    manageUser: "Gestionează contul",
    signOut: "Deconectare",
    user: "Utilizator",
    settings: "Setări",
    accountTitle: "Cont",
    accountSubtitle: "Actualizează informațiile profilului",
    avatarUrl: "URL avatar",
    saveChanges: "Salvează modificările",
    saved: "Salvat!",
    close: "Închide",

    // Chat Interface
    chatPlaceholder:
      "Întreabă-mă orice despre următoarea ta destinație de călătorie...",
    send: "Trimite",
    conversationsHistory: "Istoric Conversații",

    // Conversations History
    conversationsTitle: "Istoric Conversații",
    conversation1: "Călătorie în Japonia - Sezonul Florilor de Cireș",
    conversation2: "Aventură de Vară Europeană",
    backToChat: "Înapoi la Chat-ul Curent",

    // Destinations
    paris: "Paris",
    tokyo: "Tokyo",
    newYork: "New York",
    parisDesc:
      "Orașul Luminilor te așteaptă cu farmecul său romantic și bucătăria de clasă mondială.",
    tokyoDesc:
      "Experimentează amestecul perfect între cultura tradițională și inovația modernă.",
    newYorkDesc:
      "Orașul care nu doarme niciodată oferă posibilități nesfârșite și repere iconice.",

    // Auth Dialog
    authDialogTitle: "Autentificare",
    authDialogDescription: "Intră în cont sau creează unul nou",
    processing: "Se procesează...",

    // Login / Register forms
    welcomeBack: "Bine ai revenit",
    email: "Email",
    emailPlaceholder: "tu@exemplu.com",
    password: "Parola",
    passwordPlaceholder: "Parola ta",
    forgotPassword: "Ți-ai uitat parola?",
    loginAccount: "Autentificare",
    submitLogin: "Autentificare...",
    noAccount: "Nu ai cont? Creează unul",
    joinJourney: "Alătură-te",
    firstName: "Prenume",
    firstNamePlaceholder: "Ion",
    lastName: "Nume",
    lastNamePlaceholder: "Popescu",
    createAccount: "Creează cont",
    submitRegister: "Creare cont...",
    alreadyMember: "Ai deja cont? Autentifică-te",

    // Confirm Email modal
    confirmEmailTitle: "Confirmă emailul",
    confirmEmailDescription:
      "Contul pentru {email} nu este confirmat. Introdu tokenul primit pe email.",
    confirmationTokenPlaceholder: "Token de confirmare",
    confirm: "Confirmă",
    resendConfirmation: "Retrimite emailul",
    resendConfirmationHelp: "Trimite din nou emailul de confirmare",
    confirmSuccess: "Email confirmat. Te poți autentifica.",
    resendSuccess: "Emailul de confirmare a fost retrimis. Verifică inbox-ul.",
    confirmingEmail: "Se confirmă emailul…",
    confirmEmailError:
      "Confirmarea emailului a eșuat. Linkul poate fi expirat.",
    genericError: "A apărut o eroare. Încearcă din nou.",

    // Account Sheet
    useDefaultAvatar: "Folosește avatarul implicit (UI Avatars)",
    usingDefaultAvatar: "Folosești avatarul implicit (doar citire)",
    imgurOnlyHint: "Sunt permise doar URL-uri imgur",
    previewUrl: "URL de previzualizare",
    deleteAccount: "Șterge contul",
    deleteAccountDesc: "Șterge contul permanent.",
    confirmDelete: "Confirmă ștergerea",
    deleting: "Se șterge...",
    passwordToDelete: "Introdu parola pentru confirmare",
    deleteError: "Nu s-a putut șterge contul. Verifică parola.",
    deleteConversationsError:
      "Nu s-au putut șterge conversațiile. Verifică parola.",
    deleteConversationsSuccess: "Toate conversațiile au fost șterse.",
    deleteConversations: "Șterge conversatii",
    deleteConversationsDesc: "Șterge istoricul chat-ului.",
    canNotBeUndone: "Procesele de mai jos sunt permanente.",
    avatarHostError: "Sunt permise doar URL-uri de pe {hosts}.",

    // Forgot / Reset Password
    forgotPasswordTitle: "Ai uitat parola",
    forgotPasswordDescription:
      "Introdu adresa de email și îți vom trimite un link pentru resetarea parolei.",
    sendResetLink: "Trimite link de resetare",
    resetEmailSent:
      "Dacă emailul tău există în sistemul nostru, vei primi un link de resetare în curând. Verifică inbox-ul.",
    backToLogin: "Înapoi la autentificare",
    resetPasswordTitle: "Resetează parola",
    resetPasswordDescription:
      "Lipește tokenul de resetare primit pe email și alege o parolă nouă.",
    resetToken: "Token de resetare",
    resetTokenPlaceholder: "Lipește tokenul din email",
    newPassword: "Parolă nouă",
    newPasswordPlaceholder: "Parola ta nouă",
    resetPassword: "Resetează parola",
    passwordResetSuccess:
      "Parola a fost resetată cu succes. Te poți autentifica acum.",
    resetPasswordError: "Token invalid sau expirat. Încearcă din nou.",
    haveResetToken: "Ai deja un token de resetare?",

    // Chat agent content
    initialWelcome:
      "Bună! Sunt Agentul tău Premium de Călătorii Oombla. Te voi ghida dincolo de traseele turistice obișnuite prin planificare atentă și conexiuni privilegiate, asigurându-ți o experiență de neuitat. Pentru a începe alege o opțiune mai jos:",
    optionKnownDestinationTitle: "Cunosc destinația de călătorie",
    optionKnownDestinationDesc: "Am un loc anume în minte",
    optionPartialReqTitle: "Nu știu unde, dar știu parțial ce vreau",
    optionPartialReqDesc: "Am câteva preferințe, dar am nevoie de îndrumare",
    connectErrorSend:
      "Am probleme de conectare la serviciul de planificare a călătoriei. Te rog încearcă din nou în curând.",
    connectErrorOption:
      "Am probleme de conectare chiar acum. Te rog încearcă din nou într-un moment.",
    conversationClosed: "Această conversație este închisă.",
    backToActiveChat: "Înapoi la conversația activă",
    startNewChat: "Începe un chat nou",

    // Validations
    invalidEmail: "Adresă de email invalidă",
  },
};

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined,
);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [language, setLanguage] = useState<Language>("ro");

  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
